import {FormEvent, useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {ClearIndicatorProps} from 'react-select'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {ptBR} from 'date-fns/locale'
import styled from '@emotion/styled'
import {
  CalendarDays,
  Check,
  Filter,
  ListFilter,
  RotateCcw,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import * as API from '../../api'
import {useDistributors, useIndustries, useRegions} from '../../modules/auth'
import {ReportType} from '../../types/report-type'
import ReportFilter from './ReportFilter'
import ReportFilterSelect from './ReportFilterSelect'
import {FilterField, FilterOption, REPORT_FILTER_SCHEMAS} from './filters/reportFilterSchema'
import {
  dateString,
  isRelationshipReport,
  localDate,
  reportFilterPayload,
  restoreReportFilters,
  selectionValues,
  singleSelection,
  validateReportFilters,
} from './filters/reportFilterModel'

type Props = {
  type: ReportType
  data?: any
  show?: boolean
  onChange: (filters: any) => any
  onClose: () => void
}
const Dialog = styled(Modal)`
  --filter-surface: var(--kt-modal-bg, #fff);
  --filter-border: var(--kt-gray-200, #eff2f5);
  --filter-muted: var(--kt-gray-500, #a1a5b7);

  .modal-dialog {
    max-width: 1060px;
    width: calc(100% - 32px);
    margin: 24px auto;
  }
  .modal-content {
    border: 0;
    border-radius: 16px;
    overflow: hidden;
    background: var(--filter-surface);
    max-height: calc(100vh - 48px);
    max-height: calc(100dvh - 48px);
    box-shadow: 0 24px 80px rgba(24, 28, 50, 0.18);
  }
  .modal-header,
  .modal-footer {
    padding: 20px 24px;
    flex-shrink: 0;
    border-color: var(--filter-border);
    background: var(--filter-surface);
  }
  .filter-title {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .filter-title-icon {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    flex-shrink: 0;
    color: var(--kt-primary, #009ef7);
    background: var(--kt-primary-light, #f1faff);
  }
  .filter-title h2 {
    font-size: 19px;
    margin: 0 0 4px;
    color: var(--kt-gray-900, #181c32);
  }
  .filter-title p {
    font-size: 13px;
    margin: 0;
    color: var(--kt-gray-600, #7e8299);
  }
  .close-filters {
    width: 32px;
    height: 32px;
    padding: 0;
    flex-shrink: 0;
  }
  .modal-body {
    padding: 20px 24px;
    overflow-y: auto;
    background: var(--kt-gray-100, #f9f9f9);
  }
  .filters-toolbar {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 18px 0;
  }
  .filter-search {
    position: relative;
    flex: 1;
  }
  .filter-search > svg {
    position: absolute;
    top: 50%;
    left: 14px;
    transform: translateY(-50%);
    color: var(--filter-muted);
    pointer-events: none;
  }
  .filter-search .form-control {
    padding-left: 42px;
  }
  .filters-hint {
    font-size: 12px;
    color: var(--kt-gray-600, #7e8299);
    margin: 0;
  }
  .filters-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px 20px;
  }
  .filters-grid-period {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .filters-group {
    padding: 20px;
    margin-bottom: 16px;
    border: 1px solid var(--filter-border);
    border-radius: 12px;
    background: var(--filter-surface);
  }
  .filters-group:last-child {
    margin-bottom: 0;
  }
  .filter-section-heading {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 18px;
  }
  .filter-section-heading > svg {
    color: var(--kt-primary, #009ef7);
  }
  .filter-section-heading h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--kt-gray-800, #3f4254);
  }
  .field-heading {
    display: block;
    margin-bottom: 7px;
  }
  .field-heading label {
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    color: var(--kt-gray-700, #5e6278);
  }
  .filter-control {
    position: relative;
    min-width: 0;
  }
  .form-control,
  .select__control {
    min-height: 42px;
    border: 1px solid var(--kt-input-border-color, #e4e6ef);
    border-radius: 8px;
    background: var(--kt-input-bg, #fff);
    font-size: 13px;
    color: var(--kt-input-color, #5e6278);
  }
  .form-control {
    padding: 10px 13px;
  }
  .form-control.has-clear {
    padding-right: 42px;
  }
  .form-control:focus,
  .select__control--is-focused {
    border-color: var(--kt-primary, #009ef7);
    box-shadow: 0 0 0 3px var(--kt-primary-light, #f1faff);
  }
  .select__control:hover {
    border-color: var(--kt-gray-400, #b5b5c3);
  }
  .select__value-container {
    padding: 3px 10px;
    gap: 3px;
  }
  .select__value-container--is-multi {
    max-height: 96px;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: thin;
    scrollbar-color: var(--kt-gray-300, #e4e6ef) transparent;
  }
  .select__indicators {
    align-self: flex-start;
    height: 40px;
  }
  .select__placeholder {
    color: var(--filter-muted);
  }
  .select__multi-value {
    margin: 1px 0;
    max-width: 100%;
    border-radius: 5px;
    background: var(--kt-primary-light, #f1faff);
  }
  .select__multi-value__label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    color: var(--kt-primary, #009ef7);
    padding: 3px 6px;
  }
  .select__multi-value__remove {
    flex-shrink: 0;
    color: var(--kt-primary, #009ef7);
    border-radius: 0 5px 5px 0;
  }
  .select__multi-value__remove:hover {
    background: var(--kt-primary-light, #f1faff);
    color: var(--kt-primary-active, #0095e8);
  }
  .select__dropdown-indicator {
    color: var(--filter-muted);
    padding: 8px;
  }
  .select__menu {
    z-index: 20;
    border-radius: 8px;
    overflow: hidden;
    font-size: 13px;
    background: var(--filter-surface);
    box-shadow: 0 8px 30px rgba(24, 28, 50, 0.12);
  }
  .select__option--is-focused,
  .select__option--is-selected {
    background: var(--kt-primary-light, #f1faff);
    color: var(--kt-primary, #009ef7);
  }
  .field-clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 0;
    background: transparent;
    color: var(--filter-muted);
    border-radius: 6px;
    padding: 0;
    flex-shrink: 0;
  }
  .field-clear:hover,
  .field-clear:focus-visible {
    background: var(--kt-gray-100, #f9f9f9);
    color: var(--kt-gray-800, #3f4254);
  }
  .field-clear:focus-visible {
    outline: 2px solid var(--kt-primary, #009ef7);
    outline-offset: -2px;
  }
  .field-clear-input {
    position: absolute;
    top: 50%;
    right: 7px;
    transform: translateY(-50%);
    z-index: 2;
  }
  .select-clear {
    margin-right: 2px;
  }
  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    width: 100%;
  }
  .filter-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 42px;
    padding: 10px 13px;
    border: 1px solid var(--kt-input-border-color, #e4e6ef);
    border-radius: 8px;
    background: var(--kt-input-bg, #fff);
    color: var(--kt-gray-600, #7e8299);
    font-size: 13px;
  }
  .filter-toggle .form-switch {
    padding: 0;
    min-height: 0;
    margin: 0;
  }
  .filter-toggle .form-check-input {
    float: none;
    margin: 0;
    width: 36px;
    height: 20px;
    cursor: pointer;
  }
  .modal-footer {
    gap: 8px;
  }
  .modal-footer .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 8px;
    font-size: 13px;
    padding: 11px 16px;
  }
  .filter-active-count {
    margin-left: auto;
    margin-right: 12px;
    font-size: 12px;
    color: var(--kt-gray-600, #7e8299);
  }
  @media (max-width: 850px) {
    .filters-grid,
    .filters-grid-period {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  @media (max-width: 560px) {
    .filter-title-icon,
    .filters-hint,
    .filter-active-count {
      display: none;
    }
    .filters-grid,
    .filters-grid-period {
      grid-template-columns: minmax(0, 1fr);
    }
    .modal-dialog {
      margin: 8px auto;
      width: calc(100% - 16px);
    }
    .modal-content {
      max-height: calc(100vh - 16px);
      max-height: calc(100dvh - 16px);
    }
    .modal-header,
    .modal-body,
    .modal-footer {
      padding: 16px;
    }
    .filters-group {
      padding: 16px;
    }
    .modal-footer .btn {
      padding: 10px 12px;
    }
    .modal-footer .btn:first-of-type {
      margin-right: auto;
    }
  }
`
function ClearSelection({clearValue, selectProps}: ClearIndicatorProps<FilterOption, boolean>) {
  const label = `Limpar ${selectProps['aria-label']}`
  return (
    <button
      type='button'
      className='field-clear select-clear'
      aria-label={label}
      title={label}
      onMouseDown={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation()
        clearValue()
      }}
    >
      <X size={15} aria-hidden='true' />
    </button>
  )
}
const optionList = (values: any[] = []): FilterOption[] =>
  values
    .map((item) => (Array.isArray(item) ? item[0] : item))
    .filter((item) => item !== null && item !== undefined && item !== '')
    .map((item) => ({
      value: String(item?.value ?? item),
      label: String(item?.label ?? item),
    }))
    .filter((item) => item.value)

export default function ReportFiltersModal({type, data, show = true, onChange, onClose}: Props) {
  const distributors = useDistributors() || []
  const industries = useIndustries() || []
  const regions = useRegions() || []
  const schema = REPORT_FILTER_SCHEMAS[type]
  const [draft, setDraft] = useState(() => restoreReportFilters(type, data))
  const [remote, setRemote] = useState<Record<string, FilterOption[]>>({})
  const [loading, setLoading] = useState(false)
  const [optionsError, setOptionsError] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [retry, setRetry] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [search, setSearch] = useState('')
  const formId = `report-filters-${type}`
  useEffect(() => {
    if (show) {
      setDraft(restoreReportFilters(type, data))
      setError(null)
      setSearch('')
      setSubmitting(false)
    }
  }, [type, data, show])

  useEffect(() => {
    if (!show) return
    let active = true
    setRemote({})
    setOptionsError(false)
    const jobs: Promise<Record<string, FilterOption[]>>[] = []
    if (schema.fields.some((field) => field.source === 'businessUnits')) {
      jobs.push(
        API.getBusinessUnits(type).then((response) => {
          if (!response?.success) throw new Error('OPTIONS_FAILED')
          return {businessUnits: optionList(response.data)}
        })
      )
    }
    if (schema.fields.some((field) => field.source?.startsWith('products.'))) {
      jobs.push(
        API.getUltimoPrecoEstoqueFilters(type).then((response) => {
          if (!response?.success) throw new Error('OPTIONS_FAILED')
          return Object.fromEntries(
            Object.entries(response.data).map(([key, values]) => [
              `products.${key}`,
              optionList(values as any[]),
            ])
          )
        })
      )
    }
    if (isRelationshipReport(type)) {
      jobs.push(
        API.getLojasRelacionamentoOptions(
          type as 'lojas_quase_churn' | 'lojas_quase_fidelizadas'
        ).then((response) => {
          if (!response?.success) throw new Error('OPTIONS_FAILED')
          return Object.fromEntries(
            Object.entries(response.data).map(([key, values]) => [
              `relationship.${key}`,
              optionList(values as any[]),
            ])
          )
        })
      )
    }
    setLoading(jobs.length > 0)
    Promise.all(jobs)
      .then((results) => {
        if (active) setRemote(Object.assign({}, ...results))
      })
      .catch(() => {
        if (active) setOptionsError(true)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [type, show, retry, schema])

  const options = useMemo<Record<string, FilterOption[]>>(
    () => ({
      industries: optionList(industries),
      distributors: optionList(distributors),
      regions: optionList(regions),
      ...remote,
    }),
    [industries, distributors, regions, remote]
  )
  const payload = reportFilterPayload(type, draft, options)
  const update = (key: string, value: any) => {
    setDraft((current) => ({...current, [key]: value}))
    setError(null)
  }
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const validation = validateReportFilters(type, draft)
    setError(validation)
    if (validation || loading || optionsError || submitting) return
    setSubmitting(true)
    onClose()
    try {
      const pending = onChange(payload)
      if (pending) await pending
    } finally {
      setSubmitting(false)
    }
  }
  const fields = schema.fields.filter(
    (field) =>
      (!field.when || field.when(draft)) &&
      field.label.toLocaleLowerCase('pt-BR').includes(search.toLocaleLowerCase('pt-BR'))
  )
  const sections = Array.from(new Set(fields.map((field) => field.section)))
  const activeCount = schema.fields.filter(
    (field) =>
      (!field.when || field.when(draft)) &&
      (field.kind === 'toggle'
        ? draft[field.key] === 'SIM'
        : selectionValues(draft[field.key]).length > 0)
  ).length
  const renderField = (field: FilterField) => {
    const id = `${formId}-${field.key}`
    const value = draft[field.key]
    const items = field.options || options[field.source || ''] || []
    const selected = selectionValues(value).map(
      (item) => items.find((option) => option.value === item) || {value: item, label: item}
    )
    return (
      <div key={field.key}>
        <div className='field-heading'>
          <label htmlFor={id}>{field.label}</label>
        </div>
        <div className='filter-control'>
          {field.kind === 'multi' || field.kind === 'single' ? (
            <ReportFilterSelect
              inputId={id}
              isMulti={field.kind === 'multi'}
              isClearable={!field.required}
              backspaceRemovesValue={!field.required}
              components={{ClearIndicator: ClearSelection, IndicatorSeparator: null}}
              closeMenuOnSelect={field.kind === 'single'}
              options={items}
              value={field.kind === 'multi' ? selected : selected[0] || null}
              onChange={(items) =>
                update(
                  field.key,
                  field.kind === 'multi' ? selectionValues(items) : singleSelection(field, items)
                )
              }
              isLoading={Boolean(field.source && loading)}
              isDisabled={submitting || Boolean(field.source && (loading || optionsError))}
              placeholder={
                field.kind === 'single'
                  ? 'Selecione uma referência'
                  : 'Todos — selecione um ou mais'
              }
              noOptionsMessage={() => 'Nenhuma opção disponível'}
              classNamePrefix='select'
              aria-label={field.label}
            />
          ) : field.kind === 'date' ? (
            <DatePicker
              id={id}
              selected={localDate(value)}
              onChange={(date) => update(field.key, dateString(date as Date | null))}
              dateFormat={schema.monthly ? 'MM/yyyy' : 'dd/MM/yyyy'}
              locale={ptBR}
              className={`form-control${value ? ' has-clear' : ''}`}
              placeholderText='Sem limite'
              disabled={submitting}
              showMonthYearPicker={schema.monthly}
              maxDate={schema.monthly ? new Date(new Date().getFullYear(), 11, 31) : new Date()}
            />
          ) : field.kind === 'toggle' ? (
            <div className='filter-toggle'>
              <div className='form-check form-switch'>
                <input
                  id={id}
                  type='checkbox'
                  className='form-check-input'
                  checked={value === 'SIM'}
                  disabled={submitting}
                  onChange={(event) => update(field.key, event.target.checked ? 'SIM' : '')}
                />
              </div>
              <span>{value === 'SIM' ? 'Sim' : 'Não'}</span>
            </div>
          ) : (
            <input
              id={id}
              className={`form-control${value ? ' has-clear' : ''}`}
              value={value || ''}
              disabled={submitting}
              onChange={(event) => update(field.key, event.target.value)}
              placeholder={field.key === 'cnpj' ? 'CNPJ com letras ou números' : undefined}
            />
          )}
          {value && (field.kind === 'text' || field.kind === 'date') ? (
            <button
              type='button'
              className='field-clear field-clear-input'
              aria-label={`Limpar ${field.label}`}
              title={`Limpar ${field.label}`}
              disabled={submitting}
              onClick={() => update(field.key, '')}
            >
              <X size={15} aria-hidden='true' />
            </button>
          ) : null}
        </div>
      </div>
    )
  }
  return (
    <Dialog show={show} onHide={onClose} centered scrollable aria-labelledby={`${formId}-title`}>
      <Modal.Header>
        <div className='filter-title'>
          <span className='filter-title-icon'>
            <Filter size={22} aria-hidden='true' />
          </span>
          <div>
            <h2 id={`${formId}-title`} className='fw-bold'>
              Filtros do relatório
            </h2>
            <p>{schema.title}</p>
          </div>
        </div>
        <button
          type='button'
          className='btn btn-icon btn-light close-filters'
          aria-label='Fechar filtros'
          onClick={onClose}
        >
          <X size={18} aria-hidden='true' />
        </button>
      </Modal.Header>
      <Modal.Body>
        <ReportFilter
          key={type}
          name={type}
          filter={payload}
          setFilter={(saved: any) => {
            setDraft(restoreReportFilters(type, saved?.data))
            setError(null)
          }}
        />
        <div className='filters-toolbar'>
          <div className='filter-search'>
            <Search size={17} aria-hidden='true' />
            <label className='visually-hidden' htmlFor={`${formId}-search`}>
              Buscar filtro
            </label>
            <input
              id={`${formId}-search`}
              type='search'
              className='form-control'
              placeholder='Buscar um filtro pelo nome'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <p className='filters-hint'>Combine os filtros para refinar os resultados.</p>
        </div>
        {optionsError ? (
          <div className='alert alert-warning mt-4' role='alert'>
            Não foi possível carregar as listas de filtros.{' '}
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => setRetry((current) => current + 1)}
            >
              Tentar novamente
            </button>
          </div>
        ) : null}
        {error ? (
          <div className='alert alert-danger mt-4' role='alert'>
            {error}
          </div>
        ) : null}
        <form id={formId} onSubmit={submit}>
          {sections.map((section) => {
            const sectionFields = fields.filter((field) => field.section === section)
            const SectionIcon =
              section === 'Período'
                ? CalendarDays
                : section === 'Identificação'
                ? ListFilter
                : SlidersHorizontal
            return (
              <section key={section} className='filters-group' aria-label={section}>
                <div className='filter-section-heading'>
                  <SectionIcon size={17} aria-hidden='true' />
                  <h3>{section}</h3>
                </div>
                <div
                  className={`filters-grid${
                    section === 'Período' && sectionFields.length === 4
                      ? ' filters-grid-period'
                      : ''
                  }`}
                >
                  {sectionFields.map(renderField)}
                </div>
              </section>
            )
          })}
          {!fields.length && <p className='text-muted mt-5'>Nenhum filtro encontrado.</p>}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button
          type='button'
          className='btn btn-light me-auto'
          disabled={submitting}
          onClick={() => {
            setDraft(restoreReportFilters(type, {}, true))
            setError(null)
            setSearch('')
          }}
        >
          <RotateCcw size={15} aria-hidden='true' />
          Limpar filtros
        </button>
        <span className='filter-active-count' aria-live='polite'>
          {activeCount
            ? `${activeCount} ${activeCount === 1 ? 'filtro ativo' : 'filtros ativos'}`
            : 'Nenhum filtro ativo'}
        </span>
        <button type='button' className='btn btn-light' onClick={onClose}>
          Cancelar
        </button>
        <button
          type='submit'
          form={formId}
          className='btn btn-primary'
          disabled={loading || optionsError || submitting}
        >
          <Check size={16} aria-hidden='true' />
          {submitting ? 'Aplicando...' : 'Aplicar filtros'}
        </button>
      </Modal.Footer>
    </Dialog>
  )
}
